import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './App.css';

// Import i18n configuration
import './i18n';

// Component CSS imports - 重複を削除し整理
import './styles/base.css';
import './styles/theme.css';
import './styles/utilities.css';
import './styles/components/stepper.css';
import './styles/components/product.css';
import './styles/components/criteria.css';
import './styles/components/form-inputs.css';
import './styles/components/evaluation.css';
import './styles/components/grade.css';
import './styles/components/rubric.css';
import './styles/components/user-settings.css';
import './styles/components/save-controls.css';
import './styles/components/report-print.css';
import './styles/billing.css';
import './styles/components/toast.css';
import './styles/components/embed.css';

// Layout and Pages
import { MainLayout } from './components/Layout/MainLayout';
import { ProtectedRoute } from './components/Auth/ProtectedRoute';
import { DashboardPage } from './pages/Dashboard';
import { SettingsPage } from './pages/SettingsPage';
import { EvaluationPage } from './pages/EvaluationPage';
import { InvitePage } from './pages/Collaboration/InvitePage';
import { BillingPage } from './pages/BillingPage';
import { AuthPage } from './pages/Auth';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CorporateQuote from './pages/EnterpriseQuote/CorporateQuote';
import AcademicQuote from './pages/EnterpriseQuote/AcademicQuote';
import EducationLP from './pages/landing/Education';
import { updateApiClientUserId } from './api/client';
import { fetchCurrentUser, setUserInMemory } from './utils/auth';
import type { User } from './types';
import { ToastProvider } from './contexts/ToastContext';
import { ToastContainer } from './components/ui/Toast/ToastContainer';
import { ColorThemeProvider } from './contexts/ColorThemeContext';
// import ColorPaletteSelector from './components/ColorPaletteSelector/ColorPaletteSelector';

// Store
import { useEvaluationStore } from './store/evaluationStore';
import { useUserSettingsStore } from './store/userSettingsStore';

// Embed support
import { EmbedController } from './components/Embed/EmbedController';

// グローバルな認証状態更新用のコールバック
let globalSetAuthenticated: ((value: boolean) => void) | null = null;
let globalSetUser: ((user: User | null) => void) | null = null;

// 認証状態を外部から更新するための関数
export const updateAuthState = (authenticated: boolean, user: User | null) => {
  console.log('updateAuthState called:', { authenticated, user });
  if (globalSetAuthenticated) globalSetAuthenticated(authenticated);
  if (globalSetUser) globalSetUser(user);
};

function App() {
  const { t } = useTranslation();
  
  // 認証状態の管理
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  
  // グローバル参照を設定
  useEffect(() => {
    globalSetAuthenticated = setAuthenticated;
    globalSetUser = setUser;
    
    return () => {
      globalSetAuthenticated = null;
      globalSetUser = null;
    };
  }, []);
  
  const { products = [], criteria = [], currentExaminationId, evaluationTitle } = useEvaluationStore();
  const { userSettings, setUserSettings } = useUserSettingsStore();

  // 認証状態の初期化
  useEffect(() => {
    const initAuth = async () => {
      // まず既存のローカルユーザー情報を確認
      const cachedUser = localStorage.getItem('user');
      if (cachedUser) {
        try {
          const user = JSON.parse(cachedUser);
          setUser(user);
          setUserInMemory(user);
          setAuthenticated(true);
          updateApiClientUserId();
        } catch (error) {
          console.error('Invalid cached user data:', error);
          localStorage.removeItem('user');
        }
      }

      // サーバーでセッション状態を検証（バックグラウンドで実行）
      try {
        const serverUser = await fetchCurrentUser();
        console.log('fetchCurrentUser result:', serverUser);
        if (serverUser) {
          setUser(serverUser);
          setUserInMemory(serverUser);
          localStorage.setItem('user', JSON.stringify(serverUser));
          setAuthenticated(true);
          updateApiClientUserId();
        } else if (!cachedUser) {
          // サーバーセッションがなく、ローカルキャッシュもない場合のみログアウト
          setUserInMemory(null);
          setAuthenticated(false);
        }
        // サーバーセッションがないがローカルキャッシュがある場合は、
        // ユーザーは認証状態を保持（セッション期限切れでも継続的な体験）
      } catch (error) {
        console.error('Failed to verify server session:', error);
        // ネットワークエラー時はローカル認証状態を保持
        if (!cachedUser) {
          setAuthenticated(false);
        }
        // ローカルにユーザー情報がある場合は認証状態を維持
      }
      setAuthLoading(false);
    };

    initAuth();
  }, []);

  // ストアの復元状態を確認（デバッグ用）
  useEffect(() => {
    console.log('Current store state:', {
      productsCount: products.length,
      criteriaCount: criteria.length,
      currentExaminationId,
      evaluationTitle
    });
  }, [products.length, criteria.length, currentExaminationId, evaluationTitle]);

  // アプリ初期化時にローカルストレージから手動復元を試行
  useEffect(() => {
    const checkAndRestoreStorage = () => {
      try {
        const storedData = localStorage.getItem('evaloid-evaluation-store');
        console.log('Raw localStorage data:', storedData);
        
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          console.log('Parsed localStorage data:', parsedData);
          
          // ストアが空でローカルストレージにデータがある場合、手動で復元
          if (products.length === 0 && criteria.length === 0 && parsedData?.state) {
            console.log('Store seems empty but localStorage has data, attempting manual restore...');
            
            const state = parsedData.state;
            if (state.products && state.products.length > 0) {
              console.log('Manually restoring products:', state.products);
              // products を直接設定するのではなく、ストアのアクションを使用
              state.products.forEach((product: any) => {
                console.log('Adding product:', product);
              });
            }
            
            if (state.criteria && state.criteria.length > 0) {
              console.log('Manually restoring criteria:', state.criteria);
            }
          }
        }
      } catch (error) {
        console.error('Error checking localStorage:', error);
      }
    };

    // 少し遅延してチェック（Zustandの復元後）
    const timeoutId = setTimeout(checkAndRestoreStorage, 100);
    return () => clearTimeout(timeoutId);
  }, [products.length, criteria.length]);

  // ユーザー設定の初期化
  useEffect(() => {
    if (authenticated && user && !userSettings) {
      // 認証されたユーザーの基本情報でユーザー設定を初期化
      setUserSettings({
        username: user.fullName || '',
        email: user.email || '',
        displayName: user.fullName || '',
        securityMode: false,
      });
    }
  }, [authenticated, user, userSettings, setUserSettings]);


  // 認証チェック中の表示
  if (authLoading) {
    return (
      <div className="app-loading">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p>{t('common.initializing')}</p>
        </div>
      </div>
    );
  }

  return (
    <ColorThemeProvider>
      <ToastProvider>
        <Router>
          <EmbedController />
          <Routes>
        {/* Landing Pages */}
        <Route path="/lp/education" element={<EducationLP />} />
        
        {/* 認証ページ */}
        <Route path="/auth" element={<AuthPage />} />
        
        {/* 招待ページ（認証不要） */}
        <Route path="/invite/:token" element={<InvitePage />} />
        
        {/* 法的ページ（認証不要） */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        
        {/* エンタープライズ見積もりページ（認証不要） */}
        <Route path="/enterprise-quote/corporate" element={<CorporateQuote />} />
        <Route path="/enterprise-quote/academic" element={<AcademicQuote />} />
        
        {/* 埋め込み用評価ページ（認証不要） */}
        <Route path="/embed/evaluation" element={<EvaluationPage />} />
        <Route path="/embed/evaluation/:projectId" element={<EvaluationPage />} />
        
        {/* 保護されたルート - 認証状態を渡す */}
        <Route path="/" element={
          <ProtectedRoute authenticated={authenticated}>
            <MainLayout />
          </ProtectedRoute>
        }>
          <Route index element={<DashboardPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="evaluation" element={<EvaluationPage />} />
          <Route path="evaluation/:userId/:projectId" element={<EvaluationPage />} />
        </Route>
        
        {/* 404 - ダッシュボードにリダイレクト */}
        <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <ToastContainer />
          {/* Color Palette Selector - 開発/デモ用 */}
          {/* {import.meta.env.DEV && <ColorPaletteSelector />} */}
        </Router>
      </ToastProvider>
    </ColorThemeProvider>
  );
}

export default App;